/**
 * The website's blog (pizzagarden.ca/events/blogs, "Our Stories"), as the Website blog page edits
 * it: the API's /blog group (classes/Blog). An administrator's save goes on the website at once;
 * anyone else's becomes a request that an administrator approves in Approvals.
 *
 * Non-namespaced like every other module here. fields and seen go over the wire as JSON strings,
 * because the API speaks form-urlencoded and decodes them itself.
 */
import { shrinkImage } from "@/utils/shrinkImage";
import { tr } from "@/i18n";

const notifyError = (commit, message) =>
  commit("NOTIFICATION_MESSAGE", { status: -1, title: "Website blog", message });
const notifyDone = (commit, message) =>
  commit("NOTIFICATION_MESSAGE", { status: 1, title: "Website blog", message });

const form = (obj) => {
  const p = new URLSearchParams();
  Object.keys(obj).forEach((k) => {
    const v = obj[k];
    if (v === undefined || v === null) return;
    p.append(k, typeof v === "object" ? JSON.stringify(v) : v);
  });
  return p;
};

const state = () => ({
  posts: [],
  waitingNew: [], // new stories waiting for an administrator (all of them for administrators, else one's own)
  canEdit: false,
  canPublish: false, // an administrator: saves go on the website at once
  site: "",
  loaded: false,
  // A failed load has to be visible on the page, not just a toast that vanishes.
  error: "",
});

const mutations = {
  RESET_BLOG(s) { Object.assign(s, state()); },
  SET_BLOG(s, d) {
    s.posts = d.posts || [];
    s.waitingNew = d.new || [];
    s.canEdit = !!d.can_edit;
    s.canPublish = !!d.can_publish;
    s.site = d.site || "";
    s.loaded = true;
    s.error = "";
  },
  SET_BLOG_ERROR(s, m) { s.error = m; s.loaded = true; },
};

/** POST and the usual answer: { status, message, ... }; a network failure reads as a refusal. */
async function post(path, body) {
  try {
    const { data } = await window.$http.axios.post(path, form(body));
    return data || { status: false, message: tr("No answer from the server.") };
  } catch (e) {
    return { status: false, message: tr("Could not reach the server just now.") };
  }
}

const actions = {
  async loadBlog({ commit }) {
    try {
      const { data } = await window.$http.axios.get("blog/posts");
      if (data.status) commit("SET_BLOG", data);
      else commit("SET_BLOG_ERROR", data.message || tr("The server refused the request."));
    } catch (e) {
      commit("SET_BLOG_ERROR", tr("Could not reach the blog service. It may not be installed on this server yet."));
    }
  },

  /** One story for the editor: { post, view, my_request, waiting, can_publish }, or null. */
  async loadBlogPost({ commit }, id) {
    try {
      const { data } = await window.$http.axios.get("blog/post", { params: { id } });
      if (data.status) return data;
      notifyError(commit, data.message);
    } catch (e) {
      notifyError(commit, "Could not load the story.");
    }
    return null;
  },

  /** One request in full: { request, current, proposed, can_decide }, or null. */
  async loadBlogRequest({ commit }, id) {
    try {
      const { data } = await window.$http.axios.get("blog/request", { params: { id } });
      if (data.status) return data;
      notifyError(commit, data.message);
    } catch (e) {
      notifyError(commit, "Could not load the request.");
    }
    return null;
  },

  /**
   * Save a story: id 0 = a new one, request = the person's own waiting new story it replaces.
   * fields = only what changed, seen = what the editor started from for those fields. The
   * answer comes back whole, so the editor can show errors next to their fields.
   */
  async saveBlogPost({ commit, dispatch }, { id, request, fields, seen }) {
    const data = await post("blog/save", { id: id || 0, request: request || 0, fields, seen: seen || {} });
    if (data.status && data.changed !== false) {
      notifyDone(commit, data.message);
      dispatch("loadBlog");
      dispatch("loadApprovals");
    }
    return data;
  },

  async deleteBlogPost({ commit, dispatch }, id) {
    const data = await post("blog/delete", { id });
    if (data.status) { notifyDone(commit, data.message); dispatch("loadBlog"); dispatch("loadApprovals"); }
    else notifyError(commit, data.message);
    return data;
  },

  async moveBlogPost({ commit, dispatch }, { id, to }) {
    const data = await post("blog/move", { id, to });
    if (data.status) dispatch("loadBlog");
    else notifyError(commit, data.message);
    return data;
  },

  /** A picture for a story: made small enough to send (utils/shrinkImage), then saved by the API. The address, or null. */
  async uploadBlogImage({ commit }, file) {
    const image = await shrinkImage(file).catch(() => file);
    const fd = new FormData();
    fd.append("file", image);
    try {
      const { data } = await window.$http.axios.post("blog/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.status) return data.url;
      notifyError(commit, data.message || tr("The upload failed."));
    } catch (e) {
      notifyError(commit, "The upload failed.");
    }
    return null;
  },

  /** An administrator's yes or no on a waiting change. */
  async decideBlogRequest({ commit, dispatch }, { id, decision, note }) {
    const data = await post(decision === "approve" ? "blog/approve" : "blog/reject", { id, note: note || "" });
    if (data.status) { notifyDone(commit, data.message); dispatch("loadBlog"); }
    else notifyError(commit, data.message);
    dispatch("loadApprovals");
    return data;
  },

  /** The person who asked takes a waiting change back. */
  async withdrawBlogRequest({ commit, dispatch }, id) {
    const data = await post("blog/withdraw", { id });
    if (data.status) { notifyDone(commit, data.message); dispatch("loadBlog"); }
    else notifyError(commit, data.message);
    dispatch("loadApprovals");
    return data;
  },
};

export default { state, mutations, actions };
